import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { Option } from '@angular/aria/listbox';
import type { CellType, RowObject } from '@highcharts/dashboards/es-modules/Data/DataTable.js';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DASHBOARD_QUERY_CATALOG } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import { DASHBOARD_NO_COLUMN, DashboardWidget } from '@h20-shared/dashboards';
import {
    PulseComboboxComponent,
    PulseComboboxMatchComponent,
} from '@h20-shared/pulse-components/pulse-combobox';
import { PulseFilterChipsComponent } from '@h20-shared/pulse-components/pulse-filter-chips';
import type { FilterChip } from '@h20-shared/pulse-components/pulse-filter-chips';
import { PulseMultiselectComponent } from '@h20-shared/pulse-components/pulse-multiselect';
import { PulsePopupOptionsDirective } from '@h20-shared/pulse-components/pulse-popup';
import type { PulsePopupItem } from '@h20-shared/pulse-components/pulse-popup';
import { translated } from '@h20-shared/utils/translate-utils';
import { DASHBOARD_WIDGET_MODE } from '../dashboard-widget-mode';
import type { FilterConfig, FilterWidgetConfig } from './dashboard-filter.schema';

type FilterView = {
    readonly id: string;
    readonly column: string;
    readonly configured: boolean;
    readonly multiple: boolean;
    readonly search: boolean;
    readonly clearable: boolean;
    readonly label: string;
    readonly placeholder: string;
    readonly clearLabel: string;
    readonly options: readonly PulsePopupItem<string>[];
    readonly selected: readonly string[];
    readonly selectedText: string;
    readonly cells: ReadonlyMap<string, CellType>;
};

type FilterField = {
    readonly view: FilterView;
    readonly search: string;
    readonly options: readonly PulsePopupItem<string>[];
    readonly status: string;
};

type AppliedChip = {
    readonly chip: FilterChip;
    readonly filterId: string;
    readonly value: string;
};

function isConfigured(filter: FilterConfig): boolean {
    return filter.column !== '' && filter.column !== DASHBOARD_NO_COLUMN;
}

@Component({
    selector: 'pulse-dashboard-filter',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        Option,
        PulseComboboxComponent,
        PulseComboboxMatchComponent,
        PulseMultiselectComponent,
        PulsePopupOptionsDirective,
        PulseFilterChipsComponent,
    ],
    templateUrl: './dashboard-filter.widget.html',
    styleUrl: './dashboard-filter.widget.scss',
    host: { class: 'pulse-dashboard-widget--auto-height' },
})
export class DashboardFilterWidget extends DashboardWidget<RowObject, FilterWidgetConfig> {
    private readonly translate = inject(TranslateService);
    private readonly authoring = inject(DASHBOARD_WIDGET_MODE) === 'authoring';

    /** Bumped whenever the shared filter changes, including by another widget. */
    private readonly revision = signal(0);

    private readonly searches = signal<ReadonlyMap<string, string>>(new Map());

    constructor() {
        super();

        effect((onCleanup) => {
            const handle = this.filters();
            if (handle === undefined) {
                return;
            }
            const unsubscribe = handle.changed(() => this.revision.update((n) => n + 1));
            onCleanup(() => unsubscribe());
        });
    }

    protected readonly views = computed<FilterView[]>(() => {
        const handle = this.filters();
        // choices() reads the unfiltered table, so only new data changes the options.
        const hasData = this.state().status === 'ready';
        const queryKey = this.queryKey();
        this.revision();
        const configured = Object.entries(this.config()?.filters.items ?? {});
        const columns = queryKey === undefined ? [] : DASHBOARD_QUERY_CATALOG[queryKey].columns;
        return configured.map(([id, filter], index) => {
            const column = columns.find((candidate) => candidate.key === filter.column);
            const title =
                filter.label ||
                (column === undefined ? filter.column : translated(this.translate, column.label));
            const label =
                filter.label ||
                translated(this.translate, 'Dashboard.FilterName', {
                    index: index + 1,
                });
            const cells = new Map<string, CellType>();
            if (hasData && handle && isConfigured(filter)) {
                for (const cell of handle.choices(filter.column)) {
                    cells.set(String(cell), cell);
                }
            }
            const selected =
                hasData && handle && isConfigured(filter)
                    ? handle.selection(filter.column).map((cell) => String(cell))
                    : [];
            return {
                id,
                column: filter.column,
                configured: isConfigured(filter),
                multiple: filter.control === 'multiple',
                search: filter.search,
                clearable: filter.clearable,
                label,
                placeholder: this.fieldText(label, filter.defaultLabel),
                clearLabel: translated(this.translate, 'Dashboard.FilterClearOne', {
                    filter: label,
                }),
                options: [...cells.keys()].map((value) => ({ value, label: value })),
                selected,
                selectedText: translated(this.translate, 'Dashboard.FilterSelectedCount', {
                    count: selected.length,
                    title: selected.length === 1 ? title : filter.pluralLabel || title,
                }),
                cells,
            };
        });
    });

    protected readonly loading = computed(() => this.state().status === 'loading');

    protected readonly fields = computed<FilterField[]>(() => {
        const views = this.views();
        const searches = this.searches();
        const loading = this.loading();
        return views.map((view) => {
            const search = searches.get(view.id) ?? '';
            const query = search.trim().toLowerCase();
            const options = loading
                ? []
                : view.options.filter((option) => option.label.toLowerCase().includes(query));
            const status = loading
                ? 'Dashboard.FilterLoading'
                : options.length === 0
                  ? 'Dashboard.FilterEmpty'
                  : '';
            return { view, search, options, status };
        });
    });

    protected readonly showChips = computed(() => this.config()?.appliedSummary === true);

    protected readonly showAppliedRow = computed(
        () => this.showChips() || this.config()?.clearAll === true
    );

    protected readonly showClearAll = computed(
        () => this.config()?.clearAll === true && this.appliedChips().length > 0
    );

    protected readonly unconfigured = computed(() => {
        const views = this.views();
        return !this.authoring && (views.length === 0 || views.every((view) => !view.configured));
    });

    private readonly appliedChips = computed<AppliedChip[]>(() =>
        this.views().flatMap((view) =>
            view.selected.map((value, index) => ({
                filterId: view.id,
                value,
                chip: {
                    id: `${view.id}#${index}`,
                    label: translated(this.translate, 'Dashboard.FilterChipLabel', {
                        filter: view.label,
                        value,
                    }),
                    removeLabel: translated(this.translate, 'Dashboard.FilterRemoveChip', {
                        filter: view.label,
                        value,
                    }),
                },
            }))
        )
    );

    protected readonly chips = computed(() => this.appliedChips().map((applied) => applied.chip));

    protected onSelected(view: FilterView, values: readonly string[]): void {
        this.publish(view, values);
    }

    protected onPicked(view: FilterView, value: string | null): void {
        this.publish(view, value === null ? [] : [value]);
    }

    protected onSearch(view: FilterView, text: string): void {
        this.searches.update((searches) => new Map(searches).set(view.id, text));
    }

    protected removeChip(chipId: string): void {
        const applied = this.appliedChips().find((candidate) => candidate.chip.id === chipId);
        if (applied === undefined) {
            return;
        }
        const view = this.views().find((candidate) => candidate.id === applied.filterId);
        if (view === undefined) {
            return;
        }
        this.onSelected(
            view,
            view.selected.filter((value) => value !== applied.value)
        );
    }

    protected clearAll(): void {
        for (const view of this.views()) {
            this.publish(view, []);
        }
    }

    private fieldText(label: string, defaultLabel: string): string {
        if (defaultLabel === '') {
            return label;
        }
        return translated(this.translate, 'Dashboard.FilterFieldLabel', {
            filter: label,
            scope: defaultLabel,
        });
    }

    private publish(view: FilterView, values: readonly string[]): void {
        const handle = this.filters();
        if (handle === undefined || !view.configured) {
            return;
        }

        const cells: CellType[] = [];
        for (const value of values) {
            const cell = view.cells.get(value);
            if (cell !== undefined) {
                cells.push(cell);
            }
        }
        void handle.select(view.column, cells);
    }
}
